import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function MailButton({ onClick, testid, label = "Email" }) {
  return (
    <Button size="icon" variant="ghost" data-testid={testid} onClick={onClick} title={label} className="h-8 w-8 text-violet-300 hover:bg-violet-500/10"><Mail className="h-4 w-4" /></Button>
  );
}

export function SendMailDialog({ target, onClose }) {
  const [f, setF] = useState({ to: "", cc: "", subject: "", message: "" });
  const [from, setFrom] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!target) return;
    setF({ to: target.to || "", cc: "", subject: target.subject || "", message: target.message || "" });
    api.get("/mail/config").then((r) => setFrom(r.data.from)).catch(() => {});
    api.get("/mail/log", { params: { kind: target.kind, ref: target.ref } }).then((r) => setHistory(r.data)).catch(() => {});
  }, [target]);

  const send = async (e) => {
    e.preventDefault();
    if (!f.to.trim()) return toast.error("Enter recipient email");
    setSending(true);
    try {
      await api.post(target.endpoint, f);
      toast.success(`Email sent to ${f.to}`);
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader><DialogTitle>{target?.title || "Send Email"}</DialogTitle></DialogHeader>
        {target && (
          <form onSubmit={send} className="space-y-4">
            <p className="text-xs text-muted-foreground">From: <span className="text-foreground font-mono-sku" data-testid="mail-from">{from || "—"}</span> · Attachment: <span className="text-foreground">{target.attachment}</span></p>
            <div className="space-y-2"><Label>To</Label>
              <Input data-testid="mail-to-input" type="text" required value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} placeholder="party@example.com, another@example.com" />
              {!target.to && <p className="text-xs text-amber-400">This party has no email saved. Enter one here (and add it on the Parties page for next time).</p>}
            </div>
            <div className="space-y-2"><Label>CC</Label>
              <Input data-testid="mail-cc-input" value={f.cc} onChange={(e) => setF({ ...f, cc: e.target.value })} placeholder="Optional" /></div>
            <div className="space-y-2"><Label>Subject</Label>
              <Input data-testid="mail-subject-input" required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></div>
            <div className="space-y-2"><Label>Message</Label>
              <Textarea data-testid="mail-message-input" rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} /></div>
            {history.length > 0 && (
              <div className="space-y-1"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Previously sent</p>
                {history.slice(0, 4).map((h) => (
                  <div key={h.id} className="flex justify-between text-xs text-muted-foreground"><span>{h.sent_at.slice(0, 16).replace("T", " ")} · {h.to}</span><span className={h.status === "sent" ? "text-emerald-400" : "text-rose-400"}>{h.status}</span></div>))}
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={sending} data-testid="mail-send-btn" className="bg-violet-500 hover:bg-violet-600 text-white gap-2">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} {sending ? "Sending…" : "Send Email"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const mailTargets = {
  invoice: (inv, company) => ({
    kind: "invoice", ref: inv.invoice_no, endpoint: `/mail/invoice/${inv.id}`, title: `Email Invoice ${inv.invoice_no}`, attachment: `${inv.invoice_no}.pdf`,
    to: inv.party?.email || "", subject: `Invoice ${inv.invoice_no} from ${company || "us"}`,
    message: `Dear ${inv.party_name},\n\nPlease find attached Tax Invoice ${inv.invoice_no} dated ${inv.date} for Rs. ${Number(inv.total).toLocaleString("en-IN")}.\nBalance due: Rs. ${Number(inv.balance).toLocaleString("en-IN")}.\n\nThank you,\n${company || ""}`,
  }),
  vendorInvoice: (inv, company) => ({
    kind: "vendor_invoice", ref: inv.invoice_no, endpoint: `/mail/vendor-invoice/${inv.id}`, title: `Email Purchase Invoice ${inv.invoice_no}`, attachment: `${inv.invoice_no}.pdf`,
    to: inv.vendor?.email || "", subject: `Purchase Invoice ${inv.invoice_no} – ${company || ""}`,
    message: `Dear ${inv.vendor_name},\n\nPlease find attached our record of your invoice ${inv.invoice_no} dated ${inv.date} for Rs. ${Number(inv.total).toLocaleString("en-IN")}.\nPaid: Rs. ${Number(inv.paid_amount).toLocaleString("en-IN")} · Balance: Rs. ${Number(inv.balance).toLocaleString("en-IN")}.\n\nRegards,\n${company || ""}`,
  }),
  po: (po, company) => ({
    kind: "po", ref: po.po_no, endpoint: `/mail/po/${po.id}`, title: `Email Work Order ${po.po_no}`, attachment: `${po.po_no}.pdf`,
    to: po.vendor?.email || "", subject: `Purchase / Work Order ${po.po_no} from ${company || "us"}`,
    message: `Dear ${po.vendor_name},\n\nPlease find attached Work Order ${po.po_no} for the period ${po.start_date} to ${po.end_date || "—"}, total value Rs. ${Number(po.total_value).toLocaleString("en-IN")}.\nKindly acknowledge.\n\nRegards,\n${company || ""}`,
  }),
  ledger: (ledger, company) => ({
    kind: "ledger", ref: ledger.party.name, endpoint: `/mail/ledger/${ledger.party.id}`, title: `Email Statement – ${ledger.party.name}`, attachment: "Statement.pdf",
    to: ledger.party.email || "", subject: `Account Statement – ${ledger.party.name} – ${company || ""}`,
    message: `Dear ${ledger.party.name},\n\nPlease find attached your account statement. Closing balance: Rs. ${Number(ledger.closing_balance).toLocaleString("en-IN")} (${ledger.closing_balance >= 0 ? "receivable by us" : "payable by us"}).\n\nRegards,\n${company || ""}`,
  }),
};
